import { useState } from "react";

import { LIMITS } from "../../config/auctionRules";

import Style from "./auctionMode.module.scss";

// Tiers are stored as "everything below `upTo`", but that reads backwards when
// you are setting one up. The editor works in "from this price onwards" instead,
// so tier N's threshold is what tier N-1 stops at. The first tier always starts
// at 0 and the last always runs to the top.
const toDraft = (bidTiers) =>
  bidTiers.map((tier, i) => ({
    from: i === 0 ? 0 : bidTiers[i - 1].upTo ?? 0,
    raises: [...tier.increments],
    pending: "",
  }));

const fromDraft = (tiers) => ({
  bidTiers: tiers.map((tier, i) => ({
    upTo: i === tiers.length - 1 ? null : Number(tiers[i + 1].from),
    increments: tier.raises,
  })),
});

const draftToRules = (draft) => ({
  squad: { max: Number(draft.squadMax) },
  indians: { min: Number(draft.indiansMin), max: Number(draft.indiansMax) },
  overseas: { min: 0, max: Number(draft.overseasMax) },
  ...fromDraft(draft.tiers),
});

// Checks the combinations the backend cannot sensibly guess at, so the admin
// sees the problem before saving rather than getting silently clamped values.
const validate = (draft, rules) => {
  const problems = [];
  const { squad, indians, overseas } = rules;

  if (!squad.max || squad.max < LIMITS.squadMax.min) {
    problems.push("Squad size must be at least 1.");
  }
  if (indians.max && indians.min > indians.max) {
    problems.push("Indians minimum cannot be above the maximum.");
  }
  if (indians.max && indians.max > squad.max) {
    problems.push("Indians maximum cannot be above the squad size.");
  }
  if (overseas.max && overseas.max > squad.max) {
    problems.push("Foreigners maximum cannot be above the squad size.");
  }
  if (indians.min && overseas.max && indians.min + overseas.max > squad.max) {
    problems.push(
      `A squad of ${squad.max} cannot hold ${indians.min} Indians and ${overseas.max} foreigners.`
    );
  }

  draft.tiers.forEach((tier, i) => {
    if (!tier.raises.length) {
      problems.push(
        i === 0
          ? "The opening tier needs at least one raise."
          : `The tier from ${tier.from || "?"} L needs at least one raise.`
      );
    }
    if (i > 0) {
      const from = Number(tier.from);
      if (!from || from < 1) {
        problems.push("Every tier after the first needs a price to start at.");
      } else if (from <= Number(draft.tiers[i - 1].from)) {
        problems.push(
          `A tier starting at ${from} L must come after the one before it.`
        );
      }
    }
  });

  return [...new Set(problems)];
};

const RulesEditor = ({ rules, saving, onCancel, onSave, onReset, isCustom }) => {
  const [draft, setDraft] = useState({
    squadMax: String(rules.squad.max),
    indiansMin: String(rules.indians.min),
    indiansMax: String(rules.indians.max),
    overseasMax: String(rules.overseas.max),
    tiers: toDraft(rules.bidTiers),
  });

  const set = (field) => (e) =>
    setDraft((d) => ({ ...d, [field]: e.target.value }));

  const patchTier = (index, patch) =>
    setDraft((d) => ({
      ...d,
      tiers: d.tiers.map((tier, i) =>
        i === index ? { ...tier, ...patch } : tier
      ),
    }));

  const addRaise = (index) =>
    setDraft((d) => {
      const tier = d.tiers[index];
      const value = Math.round(Number(tier.pending));

      if (
        !Number.isFinite(value) ||
        value < LIMITS.increment.min ||
        value > LIMITS.increment.max ||
        tier.raises.includes(value) ||
        tier.raises.length >= LIMITS.incrementsPerTier.max
      ) {
        return d;
      }

      return {
        ...d,
        tiers: d.tiers.map((t, i) =>
          i === index
            ? {
                ...t,
                raises: [...t.raises, value].sort((a, b) => a - b),
                pending: "",
              }
            : t
        ),
      };
    });

  const removeRaise = (index, value) =>
    setDraft((d) => ({
      ...d,
      tiers: d.tiers.map((t, i) =>
        i === index ? { ...t, raises: t.raises.filter((r) => r !== value) } : t
      ),
    }));

  const addTier = () =>
    setDraft((d) => ({
      ...d,
      tiers: [...d.tiers, { from: "", raises: [], pending: "" }],
    }));

  const removeTier = (index) =>
    setDraft((d) => ({ ...d, tiers: d.tiers.filter((_, i) => i !== index) }));

  const parsed = draftToRules(draft);
  const problems = validate(draft, parsed);

  const numberField = (label, field, hint) => (
    <label className={Style.field}>
      <span className={Style.fieldLabel}>{label}</span>
      <input
        type="number"
        min={LIMITS.perTeam.min}
        max={LIMITS.perTeam.max}
        value={draft[field]}
        onChange={set(field)}
        className={Style.input}
      />
      <span className={Style.fieldHint}>{hint}</span>
    </label>
  );

  return (
    <div className={Style.editor}>
      <div className={Style.fieldRow}>
        {numberField("Squad max", "squadMax", "Players per team")}
        {numberField("Indians min", "indiansMin", "0 for no minimum")}
        {numberField("Indians max", "indiansMax", "0 for no limit")}
        {numberField("Foreigners max", "overseasMax", "0 for no limit")}
      </div>

      <div className={Style.tiersHead}>
        <span className={Style.fieldLabel}>Bid raises</span>
      </div>
      <div className={Style.fieldHint}>
        These are the buttons a team owner sees. Add one raise at a time. Add a
        tier if bigger raises should kick in once the bidding crosses a price.
      </div>

      {draft.tiers.map((tier, i) => {
        const full = tier.raises.length >= LIMITS.incrementsPerTier.max;

        return (
          <div className={Style.tier} key={i}>
            <div className={Style.tierWhenRow}>
              {i === 0 ? (
                <span className={Style.tierWhenText}>
                  From the base price onwards
                </span>
              ) : (
                <span className={Style.tierWhenText}>
                  Once the bid reaches
                  <input
                    type="number"
                    min="1"
                    value={tier.from}
                    onChange={(e) => patchTier(i, { from: e.target.value })}
                    placeholder="100"
                    className={`${Style.input} ${Style.inlineInput}`}
                  />
                  L and above
                </span>
              )}

              {i > 0 && (
                <button
                  type="button"
                  className={Style.removeBtn}
                  onClick={() => removeTier(i)}
                >
                  Remove tier
                </button>
              )}
            </div>

            <div className={Style.raises}>
              {tier.raises.map((raise) => (
                <span className={Style.raise} key={raise}>
                  +{raise} L
                  <button
                    type="button"
                    className={Style.raiseX}
                    onClick={() => removeRaise(i, raise)}
                    title={`Remove the +${raise} L raise`}
                  >
                    ×
                  </button>
                </span>
              ))}

              {!tier.raises.length && (
                <span className={Style.raisesEmpty}>No raises yet</span>
              )}
            </div>

            <div className={Style.addRaise}>
              <span className={Style.addRaisePlus}>+</span>
              <input
                type="number"
                min={LIMITS.increment.min}
                max={LIMITS.increment.max}
                value={tier.pending}
                disabled={full}
                onChange={(e) => patchTier(i, { pending: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addRaise(i);
                  }
                }}
                placeholder="5"
                className={`${Style.input} ${Style.raiseInput}`}
              />
              <span className={Style.addRaiseUnit}>L</span>
              <button
                type="button"
                className={Style.addRaiseBtn}
                disabled={full || !tier.pending}
                onClick={() => addRaise(i)}
              >
                Add raise
              </button>
              {full && (
                <span className={Style.fieldHint}>
                  {LIMITS.incrementsPerTier.max} raises is the most a tier can
                  offer.
                </span>
              )}
            </div>
          </div>
        );
      })}

      {draft.tiers.length < LIMITS.tiers.max && (
        <button type="button" className={Style.addTierBtn} onClick={addTier}>
          + Add a tier
        </button>
      )}

      {problems.length > 0 && (
        <div className={`${Style.notice} ${Style.noticeBad}`}>
          {problems.map((p) => (
            <div key={p}>{p}</div>
          ))}
        </div>
      )}

      <div className={Style.editorActions}>
        <button
          type="button"
          className={Style.saveBtn}
          disabled={saving || problems.length > 0}
          onClick={() => onSave(parsed)}
        >
          {saving ? "Saving..." : "Save rules"}
        </button>
        <button
          type="button"
          className={Style.linkBtn}
          disabled={saving}
          onClick={onCancel}
        >
          Cancel
        </button>
        {isCustom && (
          <button
            type="button"
            className={`${Style.linkBtn} ${Style.linkBtnQuiet}`}
            disabled={saving}
            onClick={onReset}
          >
            Reset to defaults
          </button>
        )}
      </div>
    </div>
  );
};

export default RulesEditor;
